// src/features/dashboard/socios/hooks.js
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "lib/api";
import { useDashboardSettings } from "../settings/store";

const SOCIOS_LIST_PREFIX = ["dashboard", "socios"];
const JSON_HEADERS = { "Content-Type": "application/json" };

const dashboardSociosKeys = {
  list(periodoId) {
    return ["dashboard", "socios", { periodoId }];
  },
  detail(periodoId, socioId) {
    return ["dashboard", "socio", { periodoId, socioId }];
  },
  activityOverview(periodoId, socioId) {
    return ["dashboard", "socio-activity-overview", { periodoId, socioId }];
  },
};

function invalidateList(qc) {
  qc.invalidateQueries({ queryKey: SOCIOS_LIST_PREFIX });
}


export function useDashboardSocios(_periodoId, enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardSociosKeys.list(s.periodoId),
    queryFn: () => api("/dashboard/club/socios", {}, { periodoId: s.periodoId }),
    enabled: enabled && !!s.periodoId,
    staleTime: 20_000,
  });
}

export function useDashboardSocio(socioId, enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardSociosKeys.list(s.periodoId),
    queryFn: () => api("/dashboard/club/socios", {}, { periodoId: s.periodoId }),
    select: (data) => {
      if (!Array.isArray(data)) return null;
      return data.find((row) => String(row.socioId ?? row.id) === String(socioId)) ?? null;
    },
    enabled: enabled && !!s.periodoId && !!socioId,
    staleTime: 20_000,
  });
}

export function useDashboardSocioActivityOverview(socioId, enabled = true) {
  const s = useDashboardSettings();

  return useQuery({
    queryKey: dashboardSociosKeys.activityOverview(s.periodoId, socioId),
    queryFn: () => api(`/dashboard/club/socios/${socioId}/actividad-overview`, {}, { periodoId: s.periodoId }),
    enabled: enabled && !!s.periodoId && !!socioId,
    staleTime: 20_000,
  });
}

export function usePatchDashboardSocio() {
  const s = useDashboardSettings();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ socioId, patch }) =>
      api(
        `/dashboard/club/socios/${socioId}`,
        { method: "PATCH", body: JSON.stringify(patch), headers: JSON_HEADERS },
        { periodoId: s.periodoId }
      ),
    onMutate: async ({ socioId, patch }) => {
      const key = dashboardSociosKeys.list(s.periodoId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData(key);
      if (Array.isArray(prev) && patch && typeof patch === "object") {
        qc.setQueryData(key, prev.map((row) =>
          String(row.socioId ?? row.id) === String(socioId) ? { ...row, ...patch } : row
        ));
      }
      return { prev, key };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev && ctx?.key) qc.setQueryData(ctx.key, ctx.prev);
    },
    onSuccess: () => {
      invalidateList(qc);
    },
  });
}
